import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ReceivableChargesSummary {
  totalPrincipal: number;
  totalInterest: number;
  totalFine: number;
  totalDiscount: number;
  totalFee: number;
  paymentIds: string[];
}

/**
 * Soma principal/juros/multa/desconto/tarifa por receivable_id para exibição e
 * pré-preenchimento (espelho de usePayablePaymentCharges; não altera AR).
 */
export function useReceivablePaymentCharges(receivableIds: string[]) {
  const sortedKey = [...receivableIds].sort().join(',');

  return useQuery({
    queryKey: ['receivable-payment-charges', sortedKey],
    queryFn: async (): Promise<Record<string, ReceivableChargesSummary>> => {
      if (!receivableIds.length) return {};

      const { data, error } = await supabase
        .from('receivable_payments')
        .select('id, receivable_id, payment_value, interest_value, fine_value, discount_value, fee_value')
        .in('receivable_id', receivableIds);

      if (error) throw error;

      const map: Record<string, ReceivableChargesSummary> = {};
      for (const row of data || []) {
        const principal = Number(row.payment_value) || 0;
        const interest = Number(row.interest_value) || 0;
        const fine = Number(row.fine_value) || 0;
        const discount = Number(row.discount_value) || 0;
        const fee = Number(row.fee_value) || 0;
        if (!map[row.receivable_id]) {
          map[row.receivable_id] = { totalPrincipal: 0, totalInterest: 0, totalFine: 0, totalDiscount: 0, totalFee: 0, paymentIds: [] };
        }
        map[row.receivable_id].totalPrincipal += principal;
        map[row.receivable_id].paymentIds.push(row.id);
        map[row.receivable_id].totalInterest += interest;
        map[row.receivable_id].totalFine += fine;
        map[row.receivable_id].totalDiscount += discount;
        map[row.receivable_id].totalFee += fee;
      }
      return map;
    },
    enabled: receivableIds.length > 0,
    staleTime: 60_000,
  });
}
